import React, { useState } from "react";
import PatientService from "../../services/patientService";

export interface Patient {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  father_name?: string;
  mother_name?: string;
  birth_date: string;
  age: number;
  gender: "male" | "female";
  blood_type?: string;
  phone: string;
  email?: string;
  address: string;
  emergency_contact: {
    name: string;
    phone: string;
    relationship: string;
  };
  medical_notes?: string;
  allergies?: string[];
  chronic_conditions?: string[];
  clinic_id: number;
  clinic?: {
    id: number;
    name: string;
  };
  primary_doctor_id?: number;
  primary_doctor?: {
    id: number;
    name: string;
    specialty?: string;
  };
  insurance_provider?: string;
  insurance_number?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Add these starring fields
  is_starred: boolean;
  star_reason?: string;
  starred_by?: number;
  starred_at?: string;
}

interface PatientCardProps {
  patient: Patient;
  onEdit?: (patient: Patient) => void;
  onView?: (patient: Patient) => void;
  onDelete?: (patientId: number) => void;
  onStarToggle?: (patientId: number, isStarred: boolean) => void;
  currentUserRole?: string; // 'doctor', 'admin', etc.
}

const PatientCard: React.FC<PatientCardProps> = ({
  patient,
  onEdit,
  onView,
  onDelete,
  onStarToggle,
  currentUserRole = "doctor",
}) => {
  const handleStarToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      if (patient.is_starred) {
        await PatientService.unstarPatient(patient.id);
      } else {
        const reason = window.prompt(
          "Please enter a reason for starring this patient (optional):",
        );
        await PatientService.starPatient(patient.id, reason || undefined);
      }

      // Notify parent component to refresh the patient list
      onStarToggle?.(patient.id, !patient.is_starred);
    } catch (error: any) {
      console.error("Failed to toggle star:", error);
      alert(error.message || "Failed to update patient star status");
    }
  };
  const calculateAge = (birthDate: string): number => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }

    return age;
  };
  // Check if user can create prescriptions (doctors and admins)
  const canCreatePrescription =
    currentUserRole === "doctor" || currentUserRole === "admin";

  const cardStyle: React.CSSProperties = {
    background: "#ffffff",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
    border: "1px solid #f1f5f9",
    transition: "all 0.3s ease",
    cursor: "pointer",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "16px",
  };

  const nameStyle: React.CSSProperties = {
    fontSize: "20px",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "4px",
  };

  const subInfoStyle: React.CSSProperties = {
    fontSize: "14px",
    color: "#64748b",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  };

  const statusBadgeStyle: React.CSSProperties = {
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "500",
    background: patient.is_active ? "#dcfce7" : "#fee2e2",
    color: patient.is_active ? "#166534" : "#dc2626",
  };

  const infoGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "12px",
    marginBottom: "16px",
  };

  const infoItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    color: "#475569",
  };

  const iconStyle: React.CSSProperties = {
    marginRight: "8px",
    fontSize: "16px",
  };

  const parentsInfoStyle: React.CSSProperties = {
    fontSize: "12px",
    color: "#6b7280",
    marginTop: "8px",
    fontStyle: "italic",
  };

  const actionsStyle: React.CSSProperties = {
    display: "flex",
    gap: "8px",
    marginTop: "16px",
    paddingTop: "16px",
    borderTop: "1px solid #f1f5f9",
    flexWrap: "wrap",
  };

  const buttonStyle = (color: string): React.CSSProperties => ({
    padding: "8px 16px",
    borderRadius: "8px",
    border: "none",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: color,
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    whiteSpace: "nowrap",
  });

  const prescriptionButtonStyle = (
    variant: "primary" | "secondary",
  ): React.CSSProperties => ({
    padding: "8px 16px",
    borderRadius: "8px",
    border: variant === "secondary" ? "2px solid #e5e7eb" : "none",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: variant === "primary" ? "#8b5cf6" : "#ffffff",
    color: variant === "primary" ? "#ffffff" : "#6b7280",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    whiteSpace: "nowrap",
  });

  return (
    <>
      <div
        style={cardStyle}
        onClick={() => onView?.(patient)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 8px 30px rgba(0, 0, 0, 0.12)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.08)";
        }}
      >
        <div style={headerStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div>
              <h3 style={nameStyle}>
                {patient.is_starred && (
                  <span style={{ color: "#fbbf24", marginRight: "8px" }}>
                    ⭐
                  </span>
                )}
                {patient.full_name ||
                  `${patient.first_name} ${patient.last_name}`}
              </h3>
              <div style={subInfoStyle}>
                <span>
                  👤 Born {new Date(patient.birth_date).getFullYear()}
                </span>
                <span>🩸 {patient.blood_type || "Unknown"}</span>
              </div>
              {/* Parents information */}
              {(patient.father_name || patient.mother_name) && (
                <div style={parentsInfoStyle}>
                  {patient.father_name && `👨 ${patient.father_name}`}
                  {patient.father_name && patient.mother_name && " • "}
                  {patient.mother_name && `👩 ${patient.mother_name}`}
                </div>
              )}
            </div>
          </div>

          {/* Right side with star button and status */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: "8px",
            }}
          >
            <button
              onClick={handleStarToggle}
              style={{
                background: "none",
                border: "none",
                fontSize: "20px",
                cursor: "pointer",
                padding: "4px",
                borderRadius: "4px",
                transition: "background-color 0.2s ease",
              }}
              title={
                patient.is_starred
                  ? `Starred: ${patient.star_reason || "No reason provided"}`
                  : "Click to star this patient"
              }
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f3f4f6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {patient.is_starred ? "⭐" : "☆"}
            </button>
            <span style={statusBadgeStyle}>
              {patient.is_active ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        <div style={infoGridStyle}>
          <div style={infoItemStyle}>
            <span style={iconStyle}>📞</span>
            {patient.phone}
          </div>
          <div style={infoItemStyle}>
            <span style={iconStyle}>✉️</span>
            {patient.email || "No email"}
          </div>
          <div style={infoItemStyle}>
            <span style={iconStyle}>🏠</span>
            {patient.address.slice(0, 30)}...
          </div>
          <div style={infoItemStyle}>
            <span style={iconStyle}>🚨</span>
            {patient.emergency_contact.name}
          </div>
        </div>

        {(patient.allergies?.length || patient.chronic_conditions?.length) && (
          <div style={{ marginBottom: "16px" }}>
            {patient.allergies?.length && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#dc2626",
                  marginBottom: "4px",
                }}
              >
                ⚠️ Allergies: {patient.allergies.join(", ")}
              </div>
            )}
            {patient.chronic_conditions?.length && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#ea580c",
                  marginBottom: "4px",
                }}
              >
                🏥 Conditions: {patient.chronic_conditions.join(", ")}
              </div>
            )}
          </div>
        )}
        {/* Star reason section */}
        {patient.is_starred && patient.star_reason && (
          <div
            style={{
              background: "#fef9e7",
              border: "1px solid #fbbf24",
              borderRadius: "8px",
              padding: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                color: "#92400e",
                fontWeight: "500",
              }}
            >
              <span>⭐</span>
              <span>Priority Case:</span>
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "#78350f",
                marginTop: "4px",
                fontStyle: "italic",
              }}
            >
              "{patient.star_reason}"
            </div>
            {patient.starred_at && (
              <div
                style={{
                  fontSize: "11px",
                  color: "#a16207",
                  marginTop: "4px",
                }}
              >
                Starred on {new Date(patient.starred_at).toLocaleDateString()}
              </div>
            )}
          </div>
        )}
        <div style={actionsStyle} onClick={(e) => e.stopPropagation()}>
          <button
            style={buttonStyle("#3b82f6")}
            onClick={() => onView?.(patient)}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#2563eb")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#3b82f6")}
          >
            👁️ View Details
          </button>

          <button
            style={buttonStyle("#ef4444")}
            onClick={() => {
              if (
                window.confirm("Are you sure you want to delete this patient?")
              ) {
                onDelete?.(patient.id);
              }
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#dc2626")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#ef4444")}
          >
            🗑️ Delete
          </button>
        </div>
      </div>
    </>
  );
};

export default PatientCard;
